import _ from 'lodash';
import { LIST_RUN_EVENT, TYPE_RHYTHM_EVENT_ENUM, TYPE_SPECIAL_EVENT_ENUM } from '@/shared/config';

/**
 * Captures the event filter and special-event selections as an independent copy. Deep-clones each side on its own and leaves it undefined when the corresponding input is missing.
 *
 * @param eventFilter - The current event filter state, cloned when present.
 * @param specialEventTypes - The special sub-range selected per event type, cloned when present.
 * @returns An object carrying the cloned eventFilter and specialEventTypes.
 */
const cloneSnapshot = (eventFilter: any, specialEventTypes: any) => ({
  eventFilter: eventFilter ? _.cloneDeep(eventFilter) : undefined,
  specialEventTypes: specialEventTypes ? _.cloneDeep(specialEventTypes) : undefined,
});

/**
 * Tests a numeric event value against an inclusive from and to bound. Rejects a nil value outright and skips either bound that is itself nil, so a half-open range constrains only one side.
 *
 * @param value - The value being bounded.
 * @param range - The bound, with optional from and to fields.
 * @returns True when the value falls inside the bound.
 */
const isWithinRange = (value: any, range: any) => {
  if (_.isNil(value)) {
    return false;
  }
  if (!_.isNil(range.from) && value < range.from) {
    return false;
  }
  if (!_.isNil(range.to) && value > range.to) {
    return false;
  }
  return true;
};

/**
 * Reads the value an event filter range should be compared against. Computes duration as the absolute difference between stop and start in seconds, yielding undefined when either endpoint is missing, and otherwise reads the key straight off the event.
 *
 * @param event - The event to read from.
 * @param key - The filter key, with duration derived rather than read.
 * @returns The event's value for that key, or undefined when a duration cannot be computed.
 */
const getEventValueByKey = (event: any, key: any) => {
  if (key === 'duration') {
    if (_.isNil(event?.start) || _.isNil(event?.stop)) {
      return undefined;
    }
    const diffInMs = new Date(event.stop).getTime() - new Date(event.start).getTime();
    return Math.abs(diffInMs) / 1000;
  }
  return event?.[key];
};

/**
 * Translates a tachy, brady or pause sub-range selection into the numeric HR or duration bounds an event must satisfy. Yields no bounds when nothing is selected or all HR ranges are chosen, otherwise sinus versus extreme tachy maps to maxHr 100 to 121 or 122 and up, sinus versus extreme brady to minHr 43 to 60 or up to 42, and short versus long pause to duration 2.45 to 2.94 or from 2.95.
 *
 * @param type - The event type whose sub-range selection is being expanded.
 * @param specialEventTypes - The special sub-range selected per event type.
 * @returns An object of bounds keyed by maxHr, minHr or duration, empty when unconstrained.
 */
export const generateFilterSpecialEventType = (type: any, specialEventTypes: any) => {
  if (!specialEventTypes || !specialEventTypes[type]) {
    return {};
  }
  if (specialEventTypes[type] === TYPE_SPECIAL_EVENT_ENUM.ALL_HR_RANGES) {
    return {};
  }
  if (type === TYPE_RHYTHM_EVENT_ENUM.TACHY) {
    return specialEventTypes[type] === TYPE_SPECIAL_EVENT_ENUM.SINUS_TACHY
      ? {
          maxHr: {
            from: 100,
            to: 121,
          },
        }
      : {
          maxHr: {
            from: 122,
          },
        };
  }
  if (type === TYPE_RHYTHM_EVENT_ENUM.BRADY) {
    return specialEventTypes[type] === TYPE_SPECIAL_EVENT_ENUM.SINUS_BRADY
      ? {
          minHr: {
            from: 43,
            to: 60,
          },
        }
      : {
          minHr: {
            to: 42,
          },
        };
  }
  if (type === TYPE_RHYTHM_EVENT_ENUM.PAUSE) {
    return specialEventTypes[type] === TYPE_SPECIAL_EVENT_ENUM.SHORT_PAUSE
      ? {
          duration: {
            from: 2.45,
            to: 2.94,
          },
        }
      : {
          duration: {
            from: 2.95,
          },
        };
  }
  return {};
};

/**
 * Checks an event against every bound of a special-event range filter. Treats an empty filter as a match, and otherwise requires each keyed range to contain the event's corresponding value, with duration derived from start and stop.
 *
 * @param event - The event being tested.
 * @param filter - The bounds keyed by event field.
 * @returns True when the event satisfies every range in the filter.
 */
const matchSpecialFilter = (event: any, filter: any) => {
  if (_.isEmpty(filter)) {
    return true;
  }
  return _.every(filter, (range: any, key: any) =>
    isWithinRange(getEventValueByKey(event, key), range),
  );
};

/**
 * Builds the filter snapshot stored alongside a bulk group action so it can later be replayed against individual events. Yields undefined when neither the event filter nor the special-event selections are present, and otherwise deep-clones both.
 *
 * @param eventFilter - The current event filter state.
 * @param specialEventTypes - The special sub-range selected per event type.
 * @returns The cloned snapshot, or undefined when there is nothing to capture.
 */
export const buildGroupEventFilterSnapshot = (eventFilter: any, specialEventTypes: any) => {
  if (!eventFilter && !specialEventTypes) {
    return undefined;
  }
  return cloneSnapshot(eventFilter, specialEventTypes);
};

/**
 * Replays a captured filter snapshot against a single ECG event to decide whether a bulk group action reaches it. A missing snapshot matches everything, reviewed events fail hide-reviewed, uncaptured events fail capture-only, run events need a numeric average HR above 0 and below 100, and the group's special sub-range bounds must finally contain the event.
 *
 * @param event - The event being tested.
 * @param snapshot - The captured eventFilter and specialEventTypes, or nothing to match all.
 * @param groupEventType - The event type the group action targets, selecting the sub-range bounds.
 * @returns True when the event satisfies the snapshotted filter.
 */
export const doesEventMatchFilterSnapshot = (event: any, snapshot: any, groupEventType: any) => {
  if (!snapshot) {
    return true;
  }
  const { eventFilter, specialEventTypes } = snapshot;
  if (eventFilter?.isHideReviewed && event?.isReviewed) {
    return false;
  }
  if (eventFilter?.isCapture && !event?.isCaptured) {
    return false;
  }
  if (eventFilter?.isAvgHrBelow100 && LIST_RUN_EVENT.includes(groupEventType)) {
    if (!_.isNumber(event?.avgHr) || !(event.avgHr > 0 && event.avgHr < 100)) {
      return false;
    }
  }
  const additionalFilter = generateFilterSpecialEventType(groupEventType, specialEventTypes);
  return matchSpecialFilter(event, additionalFilter);
};

/**
 * Decides whether a recorded bulk group action applies to a given ECG event. Requires both arguments, resolves the event's type from oldType, its original data or its current type, rejects a mismatch against the group's type, and then replays the group's filter snapshot against the event.
 *
 * @param event - The event the group action might reach.
 * @param groupChange - The recorded group action with its target type and filter snapshot.
 * @returns True when the event belongs to the group and passes its snapshotted filter.
 */
export const shouldApplyGroupChange = (event: any, groupChange: any) => {
  if (!groupChange || !event) {
    return false;
  }
  const eventType = event.oldType || event?.originalData?.type || event?.type;
  if (!eventType) {
    return false;
  }
  if (groupChange.groupEventType !== eventType) {
    return false;
  }
  return doesEventMatchFilterSnapshot(
    event,
    groupChange.filterSnapshot,
    groupChange.groupEventType,
  );
};
